/**
 * HotelsPicker — Overlay for hotel room cards on desktop.
 *
 * Simplified version of ProductPicker:
 *   - Always uses horizontal card layout (no list mode)
 *   - Creates RoomCard instances instead of ItemCard
 *   - No variant picker support
 *
 * Reuses the same CSS classes (.ttp-product-picker, .ttp-products-horizontal)
 * so the existing ecommerceStyles layout applies.
 */
import { RoomCard } from './RoomCard.js';

export class HotelsPicker {
  constructor(options = {}) {
    this.primaryColor = options.primaryColor || '#2563eb';
    this.onRoomBook = options.onRoomBook || (() => {});
    this.onClose = options.onClose || (() => {});

    this.element = null;
    this._cards = [];
    this._resizeObserver = null;
  }

  show(rooms, title) {
    this.hide();
    if (!rooms || rooms.length === 0) return null;

    const wrapper = document.createElement('div');
    wrapper.className = 'picker-relative';

    const picker = document.createElement('div');
    picker.className = 'ttp-product-picker';

    const header = document.createElement('div');
    header.className = 'ttp-picker-header';
    header.innerHTML = `
      <span class="ttp-picker-title">${title || 'Available Rooms'}</span>
      <button class="ttp-picker-close" aria-label="Close">\u00D7</button>
    `;
    header.querySelector('.ttp-picker-close').addEventListener('click', () => {
      this.hide();
      this.onClose();
    });
    picker.appendChild(header);

    const productsEl = document.createElement('div');
    productsEl.className = 'ttp-products-horizontal';

    this._cards = [];
    rooms.forEach(room => {
      const card = new RoomCard(room, {
        compact: false,
        primaryColor: this.primaryColor,
        onBook: (cardEl, roomData) => this.onRoomBook(cardEl, roomData),
      });
      productsEl.appendChild(card.render());
      this._cards.push(card);
    });

    picker.appendChild(productsEl);
    wrapper.appendChild(picker);
    this.element = wrapper;

    if (rooms.length > 0) {
      this._resizeObserver = new ResizeObserver(entries => {
        for (const entry of entries) {
          const w = entry.target.clientWidth;
          if (w > 0) {
            this._layoutCards(productsEl, w, rooms.length);
            this._resizeObserver.disconnect();
            this._resizeObserver = null;
          }
        }
      });
      this._resizeObserver.observe(productsEl);
    }

    return wrapper;
  }

  _layoutCards(container, containerWidth, count) {
    const cards = container.querySelectorAll('.ttp-product-card');
    if (!cards.length) return;

    const style = getComputedStyle(container);
    const padL = parseFloat(style.paddingLeft) || 12;
    const padR = parseFloat(style.paddingRight) || 12;
    const gap = parseFloat(style.gap) || 10;
    const available = containerWidth - padL - padR;

    let cardWidth;
    if (count === 1) {
      cardWidth = available;
    } else if (count === 2) {
      cardWidth = Math.floor((available - gap) / 2);
    } else {
      const peek = 28;
      cardWidth = Math.floor((available - gap - peek) / 2);
    }

    cardWidth = Math.max(cardWidth, 120);

    cards.forEach(card => {
      card.style.setProperty('width', `${cardWidth}px`, 'important');
    });
  }

  hide() {
    if (this._resizeObserver) {
      this._resizeObserver.disconnect();
      this._resizeObserver = null;
    }
    if (this.element && this.element.parentNode) {
      this.element.parentNode.removeChild(this.element);
    }
    this.element = null;
    this._cards = [];
  }

  destroy() {
    this.hide();
  }
}
